import type { WorkspaceOut } from "@sagot/api-client";
import { NAME_MAX_LENGTH, SETUP_DESCRIPTION_MAX_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Field, Icon, Input, Textarea, toast } from "@sagot/ui";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { FormAlert } from "../auth/components/form-alert";
import { useErrorMessage } from "../lib/api-errors";
import { SLUG_PATTERN, slugify } from "../lib/slug";
import { useUploadErrorMessage } from "../lib/upload-errors";
import { zodResolver } from "../lib/zod-resolver";
import { useApi } from "../session/session-provider";
import { GoalPicker } from "./goal-picker";
import { IndustryPicker } from "./industry-picker";
import { LogoPicker, uploadLogo } from "./logo-picker";
import { regionOf } from "./region-defaults";
import { RegionFields } from "./region-fields";
import { TemplateSection } from "./template-section";
import { useSetupChoices } from "./use-setup-choices";
import { useSubmitOrganization, type OrganizationValues } from "./use-submit-organization";
import { WorkspaceAddress } from "./workspace-address";

const DOMAIN = /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/\S*)?$/i;

function useOrgSchema(creating: boolean) {
  const { t } = useTranslation();
  return z.object({
    name: z.string().trim().min(1, t("auth.register.orgNameRequired")).max(NAME_MAX_LENGTH),
    website: z.string().trim().refine((v) => v === "" || DOMAIN.test(v.replace(/^https?:\/\//, "")), t("onboarding.org.websiteInvalid")),
    city: z.string().trim().max(200),
    slug: creating ? z.string().regex(SLUG_PATTERN, t("onboarding.org.slugInvalid")) : z.string(),
  });
}

interface StepOrganizationProps {
  workspace: WorkspaceOut | undefined;
  onNext: (target: { slug: string; step: number }) => void;
  dashboardHref: string;
}

/** Step 1: industry → goals → organization → template (details + its follow-up questions) with an optional description for an AI draft. */
export function StepOrganization({ workspace, onNext, dashboardHref }: StepOrganizationProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const uploadMessage = useUploadErrorMessage();
  const submit = useSubmitOrganization(workspace);
  const creating = !workspace;
  const choices = useSetupChoices(workspace);
  const [region, setRegion] = useState(() => regionOf(workspace));
  const [logo, setLogo] = useState<{ url: string | null; pending: File | null; busy: boolean }>({ url: workspace?.logo_url ?? null, pending: null, busy: false });
  const [attempted, setAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, control, setValue, formState } = useForm<OrganizationValues>({
    resolver: zodResolver(useOrgSchema(creating)),
    defaultValues: { name: workspace?.name ?? "", website: (workspace?.website ?? "").replace(/^https?:\/\//, ""), city: workspace?.city ?? "", slug: "" },
  });
  const [slug, name] = useWatch({ control, name: ["slug", "name"] });
  const labelMissing = choices.industry === "other" && !choices.industryLabel.trim();
  const blocked = !choices.industry || labelMissing || choices.missing.length > 0;

  const pickLogo = async (file: File) => {
    if (creating) { setLogo({ ...logo, pending: file }); return; }
    setLogo({ ...logo, busy: true });
    try {
      const url = await uploadLogo(api, file);
      setLogo({ url, pending: null, busy: false });
      toast.success(t("onboarding.org.logoUploaded"));
    } catch (caught) {
      setLogo({ ...logo, busy: false });
      toast.error(uploadMessage(caught, t("onboarding.org.logoFailed")));
    }
  };
  const run = (generate: boolean) =>
    handleSubmit(async (values) => {
      setAttempted(true);
      if (blocked) return;
      setError(null);
      try {
        onNext(await submit({ values, choices, region, logo: logo.pending, generate }));
      } catch (caught) {
        setError(message(caught));
      }
    }, () => { setAttempted(true); });

  return (
    <form className="space-y-8" noValidate onSubmit={(event) => void run(false)(event)}>
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{t("onboarding.org.heading")}</h1>
        <p className="mt-1 text-fg-muted">{t("onboarding.org.subtitle")}</p>
      </div>
      {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} /> : null}
      <IndustryPicker
        value={choices.industry}
        onChange={choices.setIndustry}
        label={choices.industryLabel}
        onLabelChange={choices.setIndustryLabel}
        error={attempted && !choices.industry ? t("onboarding.org.industryRequired") : undefined}
        labelError={attempted && labelMissing ? t("onboarding.org.industryOtherRequired") : undefined}
      />
      <GoalPicker goals={choices.goalsQuery.data} isPending={choices.goalsQuery.isPending} isError={choices.goalsQuery.isError} value={choices.goals} onChange={choices.setGoals} />
      <div className="card">
        <div className="card-body grid grid-cols-1 gap-5 md:grid-cols-[1fr_240px]">
          <div className="space-y-4">
            <Field label={t("onboarding.org.name")} error={formState.errors.name?.message}>
              <Input
                autoComplete="organization"
                {...register("name", { onChange: (event: { target: { value: string } }) => { if (creating && !formState.dirtyFields.slug) setValue("slug", slugify(event.target.value)); } })}
              />
            </Field>
            <WorkspaceAddress slug={workspace ? workspace.slug : slug} editable={!workspace} error={formState.errors.slug?.message} inputProps={register("slug")} />
            <Field label={t("onboarding.org.website")} hint={t("onboarding.org.websiteHint")} error={formState.errors.website?.message}>
              <div className="flex">
                <span className="inline-flex h-10 items-center rounded-l-md border border-r-0 border-line-strong bg-sunken px-3 text-sm text-fg-subtle">https://</span>
                <Input className="rounded-l-none" inputMode="url" autoComplete="url" {...register("website")} />
              </div>
            </Field>
            <RegionFields value={region} onChange={setRegion} />
            <Field label={t("onboarding.org.city")}>
              <Input autoComplete="address-level2" {...register("city")} />
            </Field>
          </div>
          <LogoPicker url={logo.url} pending={logo.pending} busy={logo.busy} orgName={name} onPick={(file) => void pickLogo(file)} onRemove={() => { setLogo({ url: null, pending: null, busy: false }); }} />
        </div>
      </div>
      <section className="card card-body space-y-3" aria-labelledby="describe-title">
        <Field label={<span id="describe-title">{t("onboarding.org.describe")}</span>} hint={t("onboarding.org.describeHint")}>
          <Textarea rows={4} maxLength={SETUP_DESCRIPTION_MAX_LENGTH} value={choices.description} onChange={(event) => { choices.setDescription(event.target.value); }} />
        </Field>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-[11px] tabular-nums text-fg-subtle">{t("onboarding.org.describeCount", { count: choices.description.length })}</span>
          <Button type="button" variant="secondary" loading={formState.isSubmitting} onClick={(event) => void run(true)(event)}>
            <Icon name="sparkles" />
            {t("onboarding.org.generate")}
          </Button>
        </div>
        <p className="text-xs text-fg-subtle">{t("onboarding.org.generateHint")}</p>
      </section>
      <TemplateSection choices={choices} country={region.country} orgName={name.trim() || t("onboarding.org.yourOrganization")} attempted={attempted} />
      <div className="flex items-center justify-between border-t border-line pt-5">
        <Button asChild variant="ghost">
          <AppLink href={dashboardHref}>{t("onboarding.skip")}</AppLink>
        </Button>
        <Button type="submit" loading={formState.isSubmitting}>
          {t("onboarding.org.next")}
          <Icon name="arrow-right" />
        </Button>
      </div>
    </form>
  );
}
